import React, { useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import cn from 'classnames';

import { pluralizeCount } from '../../helpers/formatting';
import { getShopProductUrl } from '../../helpers/helpers';
import {
  getCreationLinkActions,
  getCreationLinkFix,
  getCreationLinkNote,
  getCreationLinkProduct,
  getCreationLinkStateLabel,
  getCreationLinkVerdict,
  isCreationLinkApprovable
} from '../../helpers/url_data_agent_creation_requests_helpers';
import { getProductAdminUrl } from '../../helpers/url_data_agent_live_matches_helpers';
import { getUrlDataLink } from '../../helpers/url_data_helpers';
import CreationRequestBrandSelect from './CreationRequestBrandSelect';
import PreviewThumb from './PreviewThumb';

import './CreationRequestRow.scss';

const MAX_TITLE_CHARS = 255;

/**
 * One link of a creation batch: the product APM will create for it (or created, or why it did not), and the buttons for its state.
 * @param {boolean} canChangeProducts — edit_product, which every button on the row needs
 * @param {Function} onAction — (key, edits): edit and fix take { title, AllBrand_id } changes; approve, retry and undo take none
 */
const CreationRequestRow = ({ link, lane, isBusy, isSelected, canChangeProducts = true, onToggleSelect, onAction }) => {
  // Display data
  const { evaluation } = link;
  const product = getCreationLinkProduct(link);
  const sourceUrl = getUrlDataLink(link);
  const overrides = evaluation?.overrides || {};
  const showsProductToCreate = Boolean(evaluation?.productToCreate);
  const verdict = showsProductToCreate ? getCreationLinkVerdict(evaluation) : null;
  const possibleDuplicate = showsProductToCreate ? evaluation.agentProduct : null;
  const note = getCreationLinkNote(link, lane);
  const fix = getCreationLinkFix(link);
  const actions = canChangeProducts ? getCreationLinkActions(link) : [];

  // Editors: a ready link's title or brand, or the brand fix of a refused one
  const [editor, setEditor] = useState(null);
  const [titleDraft, setTitleDraft] = useState('');
  const [brandDraft, setBrandDraft] = useState(null);
  const isReady = link.state === 'ready';
  const canEditReady = isReady && canChangeProducts;
  const canSubmitFix = fix?.requiresBrand ? Boolean(brandDraft) : Boolean(brandDraft || titleDraft.trim());

  const closeEditor = () => setEditor(null);

  const openEditor = key => {
    setTitleDraft(key === 'title' ? product.title : '');
    setBrandDraft(key === 'fix' && fix.suggestedBrand ? { value: fix.suggestedBrand.AllBrand_id, label: fix.suggestedBrand.name } : null);
    setEditor(key);
  };

  const saveEdit = edits => {
    closeEditor();
    onAction('edit', edits);
  };

  const saveTitle = event => {
    event.preventDefault();
    saveEdit({ title: titleDraft.trim() });
  };

  const submitFix = event => {
    event.preventDefault();
    closeEditor();
    onAction('fix', _.omitBy({ AllBrand_id: brandDraft?.value, title: titleDraft.trim() || undefined }, _.isUndefined));
  };

  const handleButton = key => {
    if (key === 'edit_title') return openEditor('title');
    if (key === 'change_brand') return openEditor('brand');
    if (key === 'fix') return openEditor('fix');
    return onAction(key);
  };

  return (
    <div className={cn('creation-request-row', `state-${link.state}`)} aria-busy={isBusy}>
      <span className='crr-select'>
        {canChangeProducts && isCreationLinkApprovable(link) && (
          <input type='checkbox' checked={isSelected} disabled={isBusy} aria-label={`Select ${product.title}`} onChange={onToggleSelect} />
        )}
      </span>
      <PreviewThumb source={product.image} title={product.title} subtitle={product.brand} />
      <div className='crr-text'>
        <div className='crr-title' title={product.title}>
          {product.Product_id ? (
            <a href={getShopProductUrl(product.Product_id)} target='_blank' rel='noopener noreferrer'>
              {product.title}
            </a>
          ) : (
            product.title
          )}
          {showsProductToCreate && overrides.title && (
            <span className='crr-edited'>
              Edited
              {canEditReady && (
                <button type='button' className='crr-reset' disabled={isBusy} onClick={() => saveEdit({ title: null })}>
                  Reset
                </button>
              )}
            </span>
          )}
        </div>
        <div className='crr-meta'>
          {product.brand && <span className={cn('crr-brand', { new: evaluation?.productBrand?.isNew })}>{product.brand}</span>}
          {showsProductToCreate && overrides.AllBrand_id && (
            <span className='crr-edited'>
              Edited
              {canEditReady && (
                <button type='button' className='crr-reset' disabled={isBusy} onClick={() => saveEdit({ AllBrand_id: null })}>
                  Reset
                </button>
              )}
            </span>
          )}
          {product.categoryName && <span>{product.categoryName}</span>}
          {product.price && <span>{product.price}</span>}
          {product.Product_id && (
            <a className='mono' href={getProductAdminUrl(product.Product_id)} target='_blank' rel='noopener noreferrer'>
              #{product.Product_id}
            </a>
          )}
          {sourceUrl && (
            <a href={sourceUrl} target='_blank' rel='noopener noreferrer'>
              {link.domain || 'source page'} ↗
            </a>
          )}
          <span>{pluralizeCount(link.pinCount, 'pin')}</span>
        </div>
      </div>
      <div className='crr-agent'>
        {verdict && <span>{verdict}</span>}
        {possibleDuplicate && (
          <a className='crr-duplicate' href={getProductAdminUrl(possibleDuplicate.id)} target='_blank' rel='noopener noreferrer'>
            Possible duplicate: {possibleDuplicate.title} #{possibleDuplicate.id}
          </a>
        )}
      </div>
      <div className='crr-state'>
        <span className='crr-pill'>{getCreationLinkStateLabel(link)}</span>
        {note && (
          <span className='crr-note' title={note}>
            {note}
          </span>
        )}
      </div>
      <div className='crr-actions'>
        {actions.map(action => (
          <button
            key={action.key}
            type='button'
            className={cn('se-btn', { primary: action.primary })}
            disabled={isBusy}
            onClick={() => handleButton(action.key)}
          >
            {action.display}
          </button>
        ))}
      </div>

      {isReady && editor === 'title' && (
        <form className='crr-editor' onSubmit={saveTitle}>
          <input
            type='text'
            value={titleDraft}
            maxLength={MAX_TITLE_CHARS}
            aria-label='Product title'
            autoFocus
            onChange={event => setTitleDraft(event.target.value)}
          />
          <button type='submit' className='se-btn primary' disabled={isBusy || !titleDraft.trim()}>
            Save title
          </button>
          <button type='button' className='se-btn ghost' onClick={closeEditor}>
            Cancel
          </button>
        </form>
      )}
      {isReady && editor === 'brand' && (
        <div className='crr-editor'>
          <CreationRequestBrandSelect value={null} autoFocus onChange={option => saveEdit({ AllBrand_id: option.value })} />
          <button type='button' className='se-btn ghost' onClick={closeEditor}>
            Cancel
          </button>
        </div>
      )}
      {editor === 'fix' && fix?.kind === 'brand' && (
        <form className='crr-editor' onSubmit={submitFix}>
          <CreationRequestBrandSelect value={brandDraft} autoFocus onChange={setBrandDraft} />
          {fix.takesTitle && (
            <input
              type='text'
              value={titleDraft}
              maxLength={MAX_TITLE_CHARS}
              aria-label='Product title'
              placeholder={product.title}
              onChange={event => setTitleDraft(event.target.value)}
            />
          )}
          <button type='submit' className='se-btn primary' disabled={isBusy || !canSubmitFix}>
            Create with these details
          </button>
          <button type='button' className='se-btn ghost' onClick={closeEditor}>
            Cancel
          </button>
        </form>
      )}
    </div>
  );
};

CreationRequestRow.propTypes = {
  link: PropTypes.object.isRequired,
  lane: PropTypes.object,
  isBusy: PropTypes.bool,
  isSelected: PropTypes.bool,
  canChangeProducts: PropTypes.bool,
  onToggleSelect: PropTypes.func.isRequired,
  onAction: PropTypes.func.isRequired
};

export default CreationRequestRow;
